import React, {useMemo, useState} from 'react';
import {Alert, Badge, Button, Card, Spinner, Table} from "react-bootstrap";
import {useQuery} from "@tanstack/react-query";
import {fetchClientPortalDeliveries, fetchClientPortalDeliveriesOpen} from "../api/clientPortalApi";
import {formatCurrency, formatDate, formatMoveState} from "../utils/formatters";
import { isBefore, startOfDay } from 'date-fns';
import {Link} from "react-router-dom";
import {ClientPortalFinancials} from "./ClientPortalFinancials";

function TrackingCell({ trackingRef, trackingUrl }) {
    if (!trackingUrl) {
        return (<span className='fw-bold'>{trackingRef || '-'}</span>);
    }

    return (
        <a href={trackingUrl} target="_blank" rel="noreferrer" className='fw-bold'>
            {trackingRef || 'Tracking link'}
        </a>
    );
}

function TransferRow({ delivery }) {
    const [isOpen, setIsOpen] = useState(false);

    const totals = useMemo(() => {
        const res = {
            total: 0,
            ordered: 0,
            backorder: 0,
            ready: 0,
            currency: 'EUR',
        };

        delivery.moves.forEach((move) => {
            res.total += move.so_line_total_price.amount;
            res.ordered += move.product_uom_qty;
            res.ready += move.quantity;
            res.backorder += Math.max(0, move.product_uom_qty - move.quantity);
            res.currency = move.so_line_total_price.currency || res.currency;
        });

        return res;
    }, [delivery]);

    const trackingRef = delivery.carrier_tracking_ref === 'False' ? null : delivery.carrier_tracking_ref;
    const trackingUrl = delivery.carrier_tracking_url_manual === 'False' ? null : delivery.carrier_tracking_url_manual;
    const tracking_available = trackingRef || trackingUrl;

    return (
        <>
            <tr onClick={() => setIsOpen(!isOpen)} style={{ cursor: 'pointer' }}>
                <td>
                    <div className="d-flex align-items-center gap-2">
                        <Button variant="link" size="sm" className="p-0 text-decoration-none">
                            {isOpen ? '▼' : '▶'}
                        </Button>
                        {delivery.sales_order?.name ? (
                            <Link to={`/client/orders/${delivery.sales_order.name}`} onClick={(e) => e.stopPropagation()}>
                                {delivery.sales_order.name}
                            </Link>
                        ) : (
                            <span className="text-muted">No linked sales order</span>
                        )}
                    </div>
                    <div className="text-muted small">
                        <Link to={`/client/deliveries/${delivery.name}`} onClick={(e) => e.stopPropagation()}>
                            {delivery.name}
                        </Link>
                    </div>
                    <div className="text-muted small">
                        {delivery.partner?.name || '-'}
                    </div>
                </td>
                <td>
                    {tracking_available && <TrackingCell trackingRef={trackingRef} trackingUrl={trackingUrl} />}
                    {!tracking_available && formatMoveState(delivery.state)}
                </td>
                <td>{formatDate(delivery.scheduled_date)}</td>
                <td>{formatCurrency({ amount: totals.total, currency: totals.currency })}</td>
                <td>{totals.ordered}</td>
                <td>{totals.ready}</td>
                <td>{totals.backorder}</td>
            </tr>
            {isOpen && delivery.moves.map((move) => {
                const productNameMatch = move.product_id.name.match(/^\[(.*?)\]\s*(.*)$/);
                const sku = productNameMatch ? productNameMatch[1] : '';
                const productName = productNameMatch ? productNameMatch[2] : move.product_id.name;
                let eta = move.eta;
                if (move.state === 'assigned') {
                    eta = delivery.scheduled_date
                }
                return (
                    <tr key={move.name} className="table-light">
                        <td className="ps-4 small">
                            {sku && <div>{sku}</div>}
                            <div className="small text-muted">{productName}</div>
                            {move.has_multiple_so_matching_lines ? (
                                <Badge bg="warning" text="dark">Multiple SO lines</Badge>
                            ) : null}
                        </td>
                        <td className="small">{formatMoveState(move.state)}</td>
                        <td className="small">{formatDate(eta)}</td>
                        <td className="small">{formatCurrency(move.so_line_total_price)}</td>
                        <td className="small">{move.product_uom_qty}</td>
                        <td className="small">{move.quantity}</td>
                        <td className="small">{Math.max(0, move.product_uom_qty - move.quantity)}</td>
                    </tr>
                );
            })}
        </>
    );
}

export function ClientPortalOpenDeliveries() {
    const { data, isLoading, isError, error } = useQuery({
        queryKey: ['client-portal-open-deliveries'],
        queryFn: fetchClientPortalDeliveriesOpen,
    });
    const {withTracking, withoutTracking} = useMemo(() => {
        if (!data?.results) return {withTracking: [], withoutTracking: []};
        
        const withTracking = [];
        const withoutTracking = [];

        data.results.forEach(delivery => {
            const trackingRef = delivery.carrier_tracking_ref === 'False' ? null : delivery.carrier_tracking_ref;
            const trackingUrl = delivery.carrier_tracking_url_manual === 'False' ? null : delivery.carrier_tracking_url_manual;
            
            if (trackingRef || trackingUrl) {
                withTracking.push(delivery);
            } else {
                withoutTracking.push(delivery);
            }
        });

        return {withTracking, withoutTracking};
    }, [data?.results]);

    return (
        <Card>
            {isLoading ? (
                <div className="text-center py-5">
                    <Spinner animation="border" />
                </div>
            ) : (
                <Table responsive className="mb-0 so-table">
                    <thead>
                    <tr>
                        <th>Transfer</th>
                        <th>Status</th>
                        <th>ETA</th>
                        <th>Total price</th>
                        <th>Total qty</th>
                        <th>Reserved</th>
                        <th>On backorder</th>
                    </tr>
                    </thead>
                    <tbody>
                    {withTracking.length > 0 && (
                        <>
                            <tr className="table-success">
                                <td colSpan="7" className="fw-bold">Tracking avaialable</td>
                            </tr>
                            {withTracking.map((delivery) => (
                                <TransferRow
                                    key={delivery.name}
                                    delivery={delivery}
                                />
                            ))}
                        </>
                    )}
                    {withTracking.length > 0 && withoutTracking.length > 0 && (
                        <tr style={{ height: '50px' }}>
                            <td colSpan="7" className="p-0 bg-light"></td>
                        </tr>
                    )}
                    {withoutTracking.length > 0 && (
                        <>
                            <tr className="table-secondary">
                                <td colSpan="7" className="fw-bold">Other Deliveries</td>
                            </tr>
                            {withoutTracking.map((delivery) => (
                                <TransferRow
                                    key={delivery.name}
                                    delivery={delivery}
                                />
                            ))}
                        </>
                    )}
                    </tbody>
                </Table>
            )}
        </Card>
    );
}
